import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowUp, ArrowDown, ArrowLeft, ArrowRight,
  ArrowUpLeft, ArrowUpRight, ArrowDownLeft, ArrowDownRight,
  ZoomIn, ZoomOut, X,
} from "lucide-react";
import toast from "react-hot-toast";
import clsx from "clsx";

import { useGridStore } from "../../store/gridStore";
import { useDevicesStore } from "../../store/devicesStore";
import { ptzControl, type PtzCode } from "../../api/ptz";

interface Props {
  onClose: () => void;
}

interface ButtonSpec {
  code: PtzCode;
  label: string;
  Icon: typeof ArrowUp;
}

const DIRECTIONAL: ButtonSpec[][] = [
  [
    { code: "LeftUp", label: "Arriba-izq", Icon: ArrowUpLeft },
    { code: "Up", label: "Arriba", Icon: ArrowUp },
    { code: "RightUp", label: "Arriba-der", Icon: ArrowUpRight },
  ],
  [
    { code: "Left", label: "Izquierda", Icon: ArrowLeft },
    { code: "Up", label: "", Icon: ArrowUp },
    { code: "Right", label: "Derecha", Icon: ArrowRight },
  ],
  [
    { code: "LeftDown", label: "Abajo-izq", Icon: ArrowDownLeft },
    { code: "Down", label: "Abajo", Icon: ArrowDown },
    { code: "RightDown", label: "Abajo-der", Icon: ArrowDownRight },
  ],
];

export function PtzControlPanel({ onClose }: Props) {
  const cells = useGridStore((s) => s.cells);
  const selectedCellId = useGridStore((s) => s.selectedCellId);
  const maximizedCellId = useGridStore((s) => s.maximizedCellId);
  const devices = useDevicesStore((s) => s.devices);

  const targetCellId = maximizedCellId || selectedCellId;
  const cell = useMemo(
    () => cells.find((c) => c.cellId === targetCellId) || null,
    [cells, targetCellId],
  );
  const device = useMemo(
    () => (cell?.deviceId != null ? devices.find((d) => d.id === cell.deviceId) : null),
    [cell, devices],
  );
  const hasTarget = !!cell && cell.deviceId != null && cell.channel != null && !!device;

  const [speed, setSpeed] = useState(5);
  const activeRef = useRef<{ deviceId: number; channel: number; code: PtzCode } | null>(null);

  const sendStop = useCallback(async () => {
    const a = activeRef.current;
    if (!a) return;
    activeRef.current = null;
    try {
      await ptzControl(a.deviceId, a.channel, { action: "stop", code: a.code, speed });
    } catch {
      // Stop best-effort, no molestar al usuario si falla.
    }
  }, [speed]);

  const sendStart = useCallback(
    async (code: PtzCode) => {
      if (!hasTarget || !cell || cell.deviceId == null || cell.channel == null) return;
      if (activeRef.current) await sendStop();
      activeRef.current = { deviceId: cell.deviceId, channel: cell.channel, code };
      try {
        await ptzControl(cell.deviceId, cell.channel, { action: "start", code, speed });
      } catch (e: any) {
        activeRef.current = null;
        toast.error(e?.response?.data?.detail || "PTZ fallo");
      }
    },
    [cell, hasTarget, speed, sendStop],
  );

  // Cleanup al desmontar: detener cualquier movimiento activo.
  useEffect(() => {
    return () => {
      if (activeRef.current) {
        const a = activeRef.current;
        activeRef.current = null;
        ptzControl(a.deviceId, a.channel, { action: "stop", code: a.code }).catch(() => {});
      }
    };
  }, []);

  // Si cambia el target a otra camara con un movimiento activo, detenerlo.
  useEffect(() => {
    if (!activeRef.current) return;
    const a = activeRef.current;
    if (cell?.deviceId !== a.deviceId || cell?.channel !== a.channel) {
      activeRef.current = null;
      ptzControl(a.deviceId, a.channel, { action: "stop", code: a.code }).catch(() => {});
    }
  }, [cell?.deviceId, cell?.channel]);

  const PressButton = ({
    code, label, Icon, disabled,
  }: ButtonSpec & { disabled: boolean }) => (
    <button
      type="button"
      disabled={disabled}
      onPointerDown={(e) => { e.preventDefault(); sendStart(code); }}
      onPointerUp={sendStop}
      onPointerLeave={sendStop}
      onPointerCancel={sendStop}
      onContextMenu={(e) => e.preventDefault()}
      title={label}
      className={clsx(
        "flex items-center justify-center w-12 h-12 rounded border border-line select-none touch-none",
        disabled
          ? "bg-bg text-muted cursor-not-allowed"
          : "bg-panel text-text hover:bg-line active:bg-accent active:text-white",
      )}
    >
      <Icon size={20} />
    </button>
  );

  return (
    <aside className="w-64 flex-shrink-0 bg-panel border-l border-line flex flex-col">
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-line">
        <span className="text-xs font-semibold uppercase text-muted">PTZ</span>
        <button
          type="button"
          onClick={onClose}
          className="text-muted hover:text-text"
          title="Cerrar"
        >
          <X size={14} />
        </button>
      </div>

      <div className="px-3 py-2 border-b border-line text-xs">
        {hasTarget ? (
          <>
            <div className="text-text truncate">{device!.name}</div>
            <div className="text-muted">Canal {cell!.channel}</div>
          </>
        ) : (
          <div className="text-muted">
            Selecciona una camara haciendo click en una celda o maximizandola.
          </div>
        )}
      </div>

      <div className="p-3 flex flex-col items-center gap-2">
        {/* Cruceta 3x3: la celda central de la fila intermedia queda vacia para
            que sea el "centro" visual de la cruz. */}
        {DIRECTIONAL.map((row, ri) => (
          <div key={ri} className="flex items-center gap-2">
            {row.map((spec, ci) => {
              const isCenter = ri === 1 && ci === 1;
              if (isCenter) {
                return <div key={ci} className="w-12 h-12" />;
              }
              return (
                <PressButton key={ci} {...spec} disabled={!hasTarget} />
              );
            })}
          </div>
        ))}

        <div className="flex items-center gap-2 mt-2">
          <PressButton code="ZoomTele" label="Zoom +" Icon={ZoomIn} disabled={!hasTarget} />
          <PressButton code="ZoomWide" label="Zoom -" Icon={ZoomOut} disabled={!hasTarget} />
        </div>

        <div className="w-full mt-3">
          <div className="flex items-center justify-between text-xs text-muted">
            <span>Velocidad</span>
            <span>{speed}</span>
          </div>
          <input
            type="range"
            min={1}
            max={8}
            step={1}
            value={speed}
            onChange={(e) => setSpeed(Number(e.target.value))}
            disabled={!hasTarget}
            className="w-full accent-accent"
          />
        </div>
      </div>
    </aside>
  );
}
